import type { AIProvider } from "../ai";
import type {
  SuggestedAction,
  TriageCategory,
  TriageResult,
  TriageUrgency,
} from "../storage-schema";
import { sanitizeField } from "./sanitize";

export interface TriageInput {
  app: string;
  title: string;
  body: string;
  timestamp: number;
  category?: string; // Android category hint (e.g. "msg", "email", "call")
}

const triageSchema: Record<string, unknown> = {
  type: "object",
  properties: {
    urgency: { type: "string", enum: ["critical", "high", "medium", "low", "noise"] },
    category: {
      type: "string",
      enum: [
        "financial",
        "otp",
        "personal",
        "work",
        "delivery",
        "social",
        "promo",
        "news",
        "system",
        "calendar",
        "other",
      ],
    },
    // Reasoning scaffold: making the model commit to a yes/no here sharpens the dependent
    // suggestedAction/urgency it emits. Intentionally not surfaced (affordance reads suggestedAction).
    actionRequired: { type: "boolean" },
    suggestedAction: { type: "string", enum: ["reply", "open", "copy_otp", "none"] },
    oneLiner: { type: "string" },
  },
  required: ["urgency", "category", "actionRequired", "suggestedAction", "oneLiner"],
};

const URGENCIES: readonly TriageUrgency[] = ["critical", "high", "medium", "low", "noise"];
const CATEGORIES: readonly TriageCategory[] = [
  "financial",
  "otp",
  "personal",
  "work",
  "delivery",
  "social",
  "promo",
  "news",
  "system",
  "calendar",
  "other",
];
const ACTIONS: readonly SuggestedAction[] = ["reply", "open", "copy_otp", "none"];

function isTriageResult(v: unknown): v is TriageResult {
  if (typeof v !== "object" || v === null) return false;
  const o = v as Record<string, unknown>;
  return (
    URGENCIES.includes(o.urgency as TriageUrgency) &&
    CATEGORIES.includes(o.category as TriageCategory) &&
    typeof o.actionRequired === "boolean" &&
    ACTIONS.includes(o.suggestedAction as SuggestedAction) &&
    typeof o.oneLiner === "string"
  );
}

// First balanced {…} object, ignoring string-quoted braces — robust when the model appends trailing prose with a brace.
function firstJsonObject(s: string): string | null {
  const start = s.indexOf("{");
  if (start === -1) return null;
  let depth = 0;
  let inStr = false;
  let esc = false;
  for (let i = start; i < s.length; i++) {
    const ch = s[i];
    if (inStr) {
      if (esc) esc = false;
      else if (ch === "\\") esc = true;
      else if (ch === '"') inStr = false;
    } else if (ch === '"') {
      inStr = true;
    } else if (ch === "{") {
      depth++;
    } else if (ch === "}") {
      depth--;
      if (depth === 0) return s.slice(start, i + 1);
    }
  }
  return null;
}

// Tolerant parse: strip fences, else extract the first balanced object (Gemini Nano usually returns pure JSON but sometimes wraps/pads it).
function parseTriage(raw: string): TriageResult {
  const unfenced = raw
    .trim()
    .replace(/^```(?:json)?\s*/i, "")
    .replace(/\s*```$/i, "")
    .trim();
  let parsed: unknown;
  try {
    parsed = JSON.parse(unfenced);
  } catch {
    const span = firstJsonObject(unfenced);
    if (span === null) throw new Error("triage: model output was not JSON");
    parsed = JSON.parse(span);
  }
  if (!isTriageResult(parsed)) throw new Error("triage: output did not match schema");
  return parsed;
}

const INSTRUCTION = [
  "You triage one Android notification mirrored from a phone.",
  "Read the actual content and classify it by what matters to the user.",
  "oneLiner: max 10 words — the single thing that matters (an amount, an ask, a deadline). No app name, no fluff.",
  "Return JSON only.",
].join(" ");

/** Classify one notification; throws on model/parse/validation failure so the driver retries it next pass. */
export async function classifyNotification(
  provider: AIProvider,
  input: TriageInput,
  opts?: { signal?: AbortSignal }
): Promise<TriageResult> {
  const prompt = [
    INSTRUCTION,
    "",
    `App: ${sanitizeField(input.app)}`,
    `Title: ${sanitizeField(input.title)}`,
    `Body: ${sanitizeField(input.body)}`,
    input.category ? `Android category: ${sanitizeField(input.category)}` : "",
  ]
    .filter(Boolean)
    .join("\n");
  // Provider object + type-only AIProvider import keeps the barrel AI-runtime-free; the per-call session gives each call a fresh token budget.
  const session = await provider.createSession({ signal: opts?.signal });
  try {
    const raw = await session.prompt(prompt, {
      responseConstraint: triageSchema,
      signal: opts?.signal,
    });
    const result = parseTriage(raw);
    session.destroy();
    return result;
  } catch (err) {
    // Destroy on the error path too, then re-throw so the driver leaves the item unclassified + retries.
    session.destroy();
    throw err;
  }
}
